import { API_KEY_PREFIX } from "../../apiKeyPrefix";
import { baseUrlPattern } from "../baseUrl";
import { bearerAuth, customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

function threeBUrlPattern(apiUrl: string | undefined): string {
  return apiUrl?.trim() === "{API_URL}"
    ? ""
    : exactUrlToRegex(apiUrl, { allowHttp: true });
}

const aiContext =
  "3B's own REST API, served on the 3B app domain — not on the domain a workflow's own HTTP links are served from. Endpoints live under /api/v1; fetch /api/v1/openapi.json for the full spec. The API is unstable and likely to change, so re-read the spec rather than relying on remembered shapes.";

export const threeB = defineProduct({
  name: "Tines 3B",
  tagLine: "Automate 3B itself",
  logo,
  tags: ["featured", "engineering"],
  variants: [
    productVariant({
      identifier: "threeB",
      authLabel: "API key",
      aiContext,
      protocol: "http",
      defaultUrlPattern: ({ apiUrl }) => threeBUrlPattern(apiUrl),
      auth: (app) => bearerAuth(app.secret.apiKey),
      steps: [
        {
          title: "Get an API key",
          items: [
            {
              kind: "markdown",
              content:
                "Go to your 3B instance's settings and create an API key.",
            },
          ],
        },
        {
          title: "Enter your API key",
          items: [
            {
              kind: "input",
              label: "API key",
              placeholder: `${API_KEY_PREFIX}…`,
              required: true,
              target: { kind: "secrets", field: "apiKey" },
            },
          ],
        },
        {
          title: "Enter your API URL",
          items: [
            {
              kind: "baseUrl",
              label: "API URL",
              required: true,
              defaultValue: "{API_URL}",
              target: { kind: "configuration", field: "apiUrl" },
              helperText: "The base URL of your 3B API.",
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ apiUrl }) => `${apiUrl}/api/v1/spaces`,
      },
      publicEnvVars: ({ apiUrl }) => ({
        THREE_B_API_URL: apiUrl ?? "",
      }),
      usageHint: () =>
        "Use process.env.THREE_B_API_URL as the base URL; the proxy injects the API key.",
    }),
    productVariant({
      identifier: "threeBFederated",
      authLabel: "Service account (workflow identity)",
      aiContext,
      protocol: "http",
      defaultUrlPattern: ({ apiUrl }) => threeBUrlPattern(apiUrl),
      // The issuer and subject are server-authoritative: connectorAuth stamps
      // the issuer from the tenant API origin and the subject from the
      // running workflow, so a workflow cannot choose its own federation
      // identity. serviceAccountId is admin-chosen configuration.
      auth: (app) =>
        customAuth("selfFederation", {
          issuer: app.issuer ?? "",
          subject: app.federationSubject ?? "",
          serviceAccountId: app.config.serviceAccountId,
        }),
      steps: [
        {
          title: "Choose a service account",
          items: [
            {
              kind: "markdown",
              content:
                "This connector lets a workflow call the 3B API as a service account, without storing a long-lived API key. Each request mints a short-lived credential using the workflow's own identity — the same OIDC workload federation you'd use for GitHub Actions → AWS, with 3B as the identity provider.\n\n**Before you connect, an admin sets up federation once (Settings → Service accounts):**\n\n1. **Register an identity provider** with these exact values:\n   - **Issuer** — `{OIDC_PROVIDER_URL}`\n   - **Audience** — `3b-workflow-federation`\n2. **Add a federation binding** to the service account below, referencing that provider, with **subject** `workflow:<workflow-id>` for a single workflow — or `workflow:*` to let *every* workflow in the tenant assume it. Prefer the narrowest subject the automation needs.",
            },
            {
              kind: "input",
              label: "Service account ID",
              placeholder: "sa-…",
              required: true,
              target: { kind: "configuration", field: "serviceAccountId" },
              helperText:
                "The service account this workflow will act as. It must have a federation binding (see above) whose subject matches this workflow.",
            },
          ],
        },
        {
          title: "Enter your API URL",
          items: [
            {
              kind: "baseUrl",
              label: "API URL",
              required: true,
              defaultValue: "{API_URL}",
              target: { kind: "configuration", field: "apiUrl" },
              helperText: "The base URL of your 3B API.",
            },
          ],
        },
      ],
      // This is very bad; don’t copy it. An empty URL skips the test for this workflow-dependent connector.
      connectionTest: { protocol: "http", request: () => ({ url: "" }) },
      publicEnvVars: ({ apiUrl }) => ({
        THREE_B_API_URL: apiUrl ?? "",
      }),
      usageHint: () =>
        "Use process.env.THREE_B_API_URL as the base URL; the proxy mints and injects a short-lived service-account credential per request. There is no API key to read or print.",
    }),
    productVariant({
      identifier: "threeBFederatedCrossTenant",
      authLabel: "Service account (cross-tenant workflow identity)",
      aiContext,
      protocol: "http",
      // Scope credential injection to the escaped target base URL: a raw
      // interpolation would let `.` match any character and leak the bearer to a
      // look-alike host.
      defaultUrlPattern: ({ apiUrl }) => baseUrlPattern(apiUrl, "{API_URL}"),
      // issuer and subject are server-authoritative (connectorAuth stamps this
      // workflow's tenant API origin and subject); audience and the target
      // token URL are the admin's configuration for the tenant being federated
      // into.
      auth: (app) =>
        customAuth("selfFederation", {
          issuer: app.issuer ?? "",
          subject: app.federationSubject ?? "",
          serviceAccountId: app.config.serviceAccountId,
          audience: app.config.audience,
          tokenUrl: app.config.tokenUrl,
        }),
      steps: [
        {
          title: "Register this tenant with the target 3B",
          items: [
            {
              kind: "markdown",
              content:
                "This connector lets a workflow call **another** 3B tenant's API as one of that tenant's service accounts, using this workflow's own identity — no shared API key.\n\n**In the target tenant, an admin sets up federation once (Settings → Service accounts):**\n\n1. **Register an identity provider** whose **Issuer** is this tenant's API origin (`{OIDC_PROVIDER_URL}`) and whose **Audience** is a value they choose for you (enter the same value below).\n2. **Add a federation binding** to the target service account with a **subject** like `tenant:<this-tenant>:space:<space-url>:workflow:<workflow-id>` — or a trailing-`*` glob such as `tenant:<this-tenant>:space:<space-url>:*` to allow any workflow in that space. Scope it as narrowly as the automation needs.\n\nThe tenant and space segments are this tenant's subdomain label (the `itsec` in `itsec.3b.dev`, not its display name) and the space's URL, so a binding reads plainly — e.g. `tenant:itsec:space:prodsec:*`. Changing either **breaks** the binding until it's updated, which is deliberate: a binding never silently follows a rename.",
            },
          ],
        },
        {
          title: "Enter the target service account and audience",
          items: [
            {
              kind: "input",
              label: "Service account ID",
              placeholder: "sa-…",
              required: true,
              target: { kind: "configuration", field: "serviceAccountId" },
              helperText:
                "The service account to assume in the target tenant. It must have a federation binding whose subject matches this workflow.",
            },
            {
              kind: "input",
              label: "Audience",
              required: true,
              target: { kind: "configuration", field: "audience" },
              helperText:
                "The audience the target tenant requires on the identity token (from its provider registration).",
            },
          ],
        },
        {
          title: "Enter the target 3B URLs",
          items: [
            {
              kind: "baseUrl",
              label: "API URL",
              required: true,
              target: { kind: "configuration", field: "apiUrl" },
              helperText: "The base URL of the target 3B tenant's API.",
            },
            {
              kind: "baseUrl",
              label: "Federated token URL",
              required: true,
              target: { kind: "configuration", field: "tokenUrl" },
              helperText:
                "The target tenant's token exchange endpoint, e.g. https://target.example.com/api/v1/auth/federated-token.",
            },
          ],
        },
      ],
      // This is very bad; don’t copy it. An empty URL skips the test for this workflow-dependent connector.
      connectionTest: { protocol: "http", request: () => ({ url: "" }) },
      publicEnvVars: ({ apiUrl }) => ({
        THREE_B_API_URL: apiUrl ?? "",
      }),
      usageHint: () =>
        "Use process.env.THREE_B_API_URL as the base URL; the proxy mints this workflow's identity, exchanges it at the target tenant for a service-account credential, and injects it per request. No credential is visible to your code.",
    }),
  ],
});
